import React from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/Modal/Modal';
import Button from '@/components/forms/Button/Button';
import styles from './InvoiceModal.module.css';
import Flex from '@/components/Flex/Flex';
import IconPrintGoogle from '@/components/icons/IconPrintGoogle';
import IconDownloadBox from '@/components/icons/IconDownloadBox';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';

interface InvoiceItem {
  date: string;
  description: string;
  quantity: number;
  price: number;
  discount: number;
  total: number;
}

interface InvoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
  onPrint: () => void;
  onDownloadPdf: () => void;
  isLoading?: boolean;
  
  // Invoice details
  invoiceNumber: string;
  billedOn: string;
  terms: string;
  dueOn: string;
  
  // Company details
  companyName: string;
  companyAddress1: string;
  companyAddress2: string;
  companyCountry: string;
  companyEmail: string;
  
  // Bill to details
  billToName: string;
  billToAddress1: string;
  billToAddress2: string;
  billToCountry: string;
  
  // Invoice items
  items: InvoiceItem[];
  
  // Totals
  subtotal: number;
  total: number;
  paid: number;
  amountDue: number;
  
  // Notes
  notes: string;
}

export default function InvoiceModal({
  open,
  onOpenChange,
  onClose,
  onPrint,
  onDownloadPdf,
  isLoading = false,
  invoiceNumber,
  billedOn,
  terms,
  dueOn,
  companyName,
  companyAddress1,
  companyAddress2,
  companyCountry,
  companyEmail,
  billToName,
  billToAddress1,
  billToAddress2,
  billToCountry,
  items,
  subtotal,
  total,
  paid,
  amountDue,
  notes
}: InvoiceModalProps) {
  
  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)} USD`;
  };
  
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      className={styles.modal}
    >
      <ModalHeader divider={false} className={styles.modalHeader}>
        <svg width="107" height="16" viewBox="0 0 107 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_1039_1375)">
          <path fillRule="evenodd" clipRule="evenodd" d="M0.119701 3.61867C0.053592 3.61867 0 3.56508 0 3.49897V0.14735C0 0.0812409 0.053592 0.0276489 0.119701 0.0276489H15.0623C15.5113 0.0276489 15.9461 0.0881808 16.3591 0.201528C18.0162 0.656352 19.3214 1.96157 19.7762 3.61867C19.8896 4.03166 19.9501 4.46646 19.9501 4.91541C19.9501 5.36441 19.8896 5.7992 19.7762 6.21217C19.5934 6.87835 19.2731 7.48766 18.8478 8.00768C19.2731 8.52774 19.5934 9.13706 19.7762 9.80319C19.8896 10.2162 19.9501 10.651 19.9501 11.1C19.9501 11.5489 19.8896 11.9837 19.7762 12.3967C19.3214 14.0538 18.0162 15.3591 16.3591 15.8139C15.9461 15.9272 15.5113 15.9877 15.0623 15.9877H0.119701C0.053592 15.9877 0 15.9341 0 15.868V6.33187C0 6.2658 0.053592 6.21217 0.119701 6.21217H16.3591V3.61867H0.119701ZM16.3591 12.3967V9.80319H3.59102V12.3967H16.3591Z" fill="var(--static-text-strong)"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M37.9052 15.8139C39.5623 15.3591 40.8674 14.0538 41.3223 12.3967C41.4356 11.9837 41.4963 11.5489 41.4963 11.1V4.91541C41.4963 4.46646 41.4356 4.03166 41.3223 3.61867C40.8674 1.96157 39.5623 0.656352 37.9052 0.201529C37.4923 0.0881812 37.0574 0.0276489 36.6085 0.0276489H26.4339C25.985 0.0276489 25.5501 0.0881812 25.1372 0.201529C23.4801 0.656352 22.1748 1.96157 21.72 3.61867C21.6067 4.03166 21.5461 4.46646 21.5461 4.91541V11.1C21.5461 11.5489 21.6067 11.9837 21.72 12.3967C22.1748 14.0538 23.4801 15.3591 25.1372 15.8139C25.5501 15.9272 25.985 15.9877 26.4339 15.9877H36.6085C37.0574 15.9877 37.4923 15.9272 37.9052 15.8139ZM37.9052 12.3967V3.61867L25.1372 3.61867V12.3967H37.9052Z" fill="var(--static-text-strong)"/>
          <path d="M43.212 3.61867C43.1458 3.61867 43.0923 3.56508 43.0923 3.49897V0.147352C43.0923 0.0812425 43.1458 0.0276507 43.212 0.0276507L46.4359 0.0276504C47.8807 0.0276502 49.2517 0.66685 50.1802 1.77363L53.0673 5.21435L55.9545 1.77362C56.883 0.66685 58.254 0.0276492 59.6988 0.0276491L62.9227 0.0276489C62.9889 0.0276489 63.0424 0.0812409 63.0424 0.14735V3.49897C63.0424 3.56508 62.9889 3.61867 62.9227 3.61867H59.0939L55.4111 8.00764L59.0943 12.3967H62.9227C62.9889 12.3967 63.0424 12.4503 63.0424 12.5164V15.868C63.0424 15.9341 62.9889 15.9877 62.9227 15.9877H59.6988C58.254 15.9877 56.8834 15.3485 55.9545 14.2418L53.0673 10.801L50.1802 14.2418C49.2513 15.3485 47.8807 15.9877 46.4359 15.9877H43.212C43.1458 15.9877 43.0923 15.9341 43.0923 15.868V12.5164C43.0923 12.4503 43.1458 12.3967 43.212 12.3967H47.0404L50.7236 8.00764L47.0408 3.61867H43.212Z" fill="var(--static-text-strong)"/>
          <path d="M84.4689 0.0276489C84.5351 0.0276489 84.5886 0.0812409 84.5886 0.14735V3.49897C84.5886 3.56508 84.5351 3.61867 84.4689 3.61867H68.2295V6.21217H84.4689C84.5351 6.21217 84.5886 6.2658 84.5886 6.33187V9.68349C84.5886 9.74961 84.5351 9.80319 84.4689 9.80319H68.2295V12.3967H84.4689C84.5351 12.3967 84.5886 12.4503 84.5886 12.5164V15.868C84.5886 15.9341 84.5351 15.9877 84.4689 15.9877H69.5262C69.0773 15.9877 68.6424 15.9272 68.2295 15.8139C66.5724 15.3591 65.2673 14.0538 64.8124 12.3967C64.6991 11.9837 64.6384 11.5489 64.6384 11.1V4.91541C64.6384 4.46646 64.6991 4.03166 64.8124 3.61867C65.2673 1.96157 66.5724 0.656352 68.2295 0.201529C68.6424 0.0881812 69.0773 0.0276489 69.5262 0.0276489H84.4689Z" fill="var(--static-text-strong)"/>
          <path d="M86.3043 15.9877C86.238 15.9877 86.1846 15.9341 86.1846 15.868V0.14735C86.1846 0.0812405 86.238 0.0276489 86.3043 0.0276489H101.247C101.696 0.0276489 102.131 0.0881812 102.544 0.201529C104.201 0.656352 105.506 1.96157 105.961 3.61867C106.074 4.03166 106.135 4.46646 106.135 4.91541V15.868C106.135 15.9341 106.081 15.9877 106.015 15.9877H102.663C102.597 15.9877 102.544 15.9341 102.544 15.868V3.61867H97.9551V15.868C97.9551 15.9341 97.9017 15.9877 97.8354 15.9877H94.4838C94.4176 15.9877 94.3641 15.9341 94.3641 15.868V3.61867H89.7756V15.868C89.7756 15.9341 89.7221 15.9877 89.6559 15.9877H86.3043Z" fill="var(--static-text-strong)"/>
          </g>
          <defs>
          <clipPath id="clip0_1039_1375">
          <rect width="106.135" height="16" fill="white"/>
          </clipPath>
          </defs>
        </svg>
      </ModalHeader>
      
      <ModalBody className={styles.modalBody}>
        {isLoading ? (
          <Flex justify="center" align="center" style={{ minHeight: '300px' }}>
            <LoadingSpinner />
          </Flex>
        ) : (
          <>
            <Flex justify="space-between" className={styles.invoiceHeader}>
              <div className={styles.companyInfo}>
                <h3>{companyName}</h3>
                <p>{companyAddress1}</p>
                <p>{companyAddress2}</p>
                <p>{companyCountry}</p>
                <p className={styles.email}>Email: {companyEmail}</p>
              </div>
              
              <div className={styles.billToInfo}>
                <h4>Bill To</h4>
                <p>{billToName}</p>
                <p>{billToAddress1}</p>
                <p>{billToAddress2}</p>
                <p>{billToCountry}</p>
              </div>
              
              <Flex className={styles.invoiceInfo} direction='column' gap={2}>
                <Flex justify="space-between">
                  <p className={styles.label}>Invoice #:</p>
                  <p className={styles.value}>{invoiceNumber}</p>
                </Flex>
                <Flex justify="space-between">
                  <p className={styles.label}>Billed On:</p>
                  <p className={styles.value}>{billedOn}</p>
                </Flex>
                <Flex justify="space-between">
                  <p className={styles.label}>Terms:</p>
                  <p className={styles.value}>{terms}</p>
                </Flex>
                <Flex justify="space-between">
                  <p className={styles.label}>Due On:</p>
                  <p className={styles.value}>{dueOn}</p>
                </Flex>
              </Flex>
            </Flex>
            
            <div className={styles.invoiceTable}>
              <table>
                <thead>
                  <tr>
                    <th className={styles.dateColumn}>Date</th>
                    <th className={styles.descriptionColumn}>Description</th>
                    <th className={styles.qtyColumn}>Qty</th>
                    <th className={styles.priceColumn}>Price</th>
                    <th className={styles.discountColumn}>Discount</th>
                    <th className={styles.totalColumn}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.date}</td>
                      <td>{item.description}</td>
                      <td>{item.quantity}</td>
                      <td>${item.price.toFixed(2)}</td>
                      <td>(${item.discount.toFixed(2)})</td>
                      <td>${item.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className={styles.totals}>
              <Flex justify="space-between">
                <p className={styles.label}>Subtotal:</p>
                <p>{formatCurrency(subtotal)}</p>
              </Flex>
              <Flex justify="space-between">
                <p className={styles.label}>Total:</p>
                <p>{formatCurrency(total)}</p>
              </Flex>
              <Flex justify="space-between">
                <p className={styles.label}>Paid:</p>
                <p>{formatCurrency(paid)}</p>
              </Flex>
              <div className={styles.amountDue}>
                <Flex justify="space-between">
                  <p className={styles.label}>Amount Due:</p>
                  <p>{formatCurrency(amountDue)}</p>
                </Flex>
              </div>
            </div>
            
            <div className={styles.notes}>
              <p className={styles.notesLabel}>Notes:</p>
              <p>{notes}</p>
            </div>
          </>
        )}
      </ModalBody>
      
      <ModalFooter className={styles.footer}>
        <Button 
          type="secondary" 
          onClick={onClose}
        >
          Close
        </Button>
        <Flex gap={2}>
          <Button 
            type="primary" 
            onClick={onPrint}
          >
            <IconPrintGoogle /> Print
          </Button>
          <Button 
            type="primary" 
            onClick={onDownloadPdf}
          >
            <IconDownloadBox /> PDF
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
}